import type { SponsoredBrandAsset, SponsoredBrandStore } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldLabel, FieldLegend, FieldSet } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SponsoredBrandsAdPreview } from "./AdPreview";
import { AssetSelect } from "./AssetSelect";
import { requiredLabel, TextField } from "./FormFields";
import type { AdFormat } from "./sponsored-brands-model";

export function CreativeSection({
  adFormat,
  requiresBrandCreative,
  adName,
  onAdNameChange,
  errors,
  brandName,
  onBrandNameChange,
  headline,
  onHeadlineChange,
  logoAssets,
  logoAssetId,
  onLogoAssetChange,
  customImageAssets,
  customImageAssetId,
  onCustomImageAssetChange,
  advertisedAsins,
  creativeAsins,
  onCreativeAsinsChange,
  selectedStore,
  spotlightPageIds,
  onSpotlightPageIdsChange,
  spotlightPageAsins,
  onSpotlightPageAsinsChange,
  videoAssets,
  videoAssetId,
  onVideoAssetChange,
}: {
  adFormat: AdFormat;
  requiresBrandCreative: boolean;
  adName: string;
  onAdNameChange: (value: string) => void;
  errors: Partial<
    Record<
      "adName" | "brandName" | "headline" | "logo" | "customImage" | "video" | "products",
      string
    >
  >;
  brandName: string;
  onBrandNameChange: (value: string) => void;
  headline: string;
  onHeadlineChange: (value: string) => void;
  logoAssets: SponsoredBrandAsset[];
  logoAssetId: string | null;
  onLogoAssetChange: (value: string | null) => void;
  customImageAssets: SponsoredBrandAsset[];
  customImageAssetId: string | null;
  onCustomImageAssetChange: (value: string | null) => void;
  advertisedAsins: string[];
  creativeAsins: string[];
  onCreativeAsinsChange: (value: string[]) => void;
  selectedStore: SponsoredBrandStore | null;
  spotlightPageIds: string[];
  onSpotlightPageIdsChange: (value: string[]) => void;
  spotlightPageAsins: Record<string, string>;
  onSpotlightPageAsinsChange: (value: Record<string, string>) => void;
  videoAssets: SponsoredBrandAsset[];
  videoAssetId: string | null;
  onVideoAssetChange: (value: string | null) => void;
}) {
  const { t } = useTranslation("campaignCreation");
  return (
    <Card data-slot="sb-creative-section" className="w-full max-w-[960px] self-center">
      <CardHeader>
        <h3 className="flex items-center gap-2.5">
          <span aria-hidden="true" className="h-5 w-1 rounded-full bg-primary" />
          <CardTitle>{t("sb.creative.title")}</CardTitle>
        </h3>
      </CardHeader>
      <CardContent className="grid grid-cols-[1fr_0.9fr] gap-6">
        <div className="flex flex-col gap-4">
          <TextField
            name="adName"
            label={t("sb.creative.adName")}
            value={adName}
            onChange={onAdNameChange}
            error={errors.adName}
            required
          />
          {adFormat === "video" ? (
            <>
              <p className="text-sm text-muted-foreground">{t("sb.creative.videoGuidance")}</p>
              <AssetSelect
                label={t("sb.creative.chooseVideo")}
                assets={videoAssets}
                value={videoAssetId}
                onValueChange={onVideoAssetChange}
                error={errors.video}
                required
              />
              <div className="whitespace-pre-line rounded-lg bg-muted/40 p-4 text-sm text-muted-foreground">
                <p className="font-medium text-foreground">{t("sb.creative.videoRequirements")}</p>
                {t("sb.creative.videoSpecs")}
              </div>
              {requiresBrandCreative ? (
                <>
                  <FieldSet>
                    <FieldLegend>{t("sb.creative.brandNameLogo")}</FieldLegend>
                    <TextField
                      name="brandName"
                      label={t("sb.creative.brandName")}
                      value={brandName}
                      onChange={onBrandNameChange}
                      error={errors.brandName}
                      required
                    />
                  </FieldSet>
                  <AssetSelect
                    label={t("sb.creative.chooseAssets")}
                    assets={logoAssets}
                    value={logoAssetId}
                    onValueChange={onLogoAssetChange}
                    error={errors.logo}
                    required
                  />
                  <TextField
                    name="headline"
                    label={t("sb.creative.headline")}
                    value={headline}
                    onChange={onHeadlineChange}
                    error={errors.headline}
                    required
                  />
                </>
              ) : null}
            </>
          ) : (
            <Accordion multiple={false} defaultValue={["brand"]}>
              <AccordionItem value="brand">
                <AccordionTrigger>{requiredLabel(t("sb.creative.brandNameLogo"))}</AccordionTrigger>
                <AccordionContent className="flex flex-col gap-4">
                  <TextField
                    name="brandName"
                    label={t("sb.creative.brandName")}
                    value={brandName}
                    onChange={onBrandNameChange}
                    error={errors.brandName}
                    required
                  />
                  <AssetSelect
                    label={t("sb.creative.chooseAssets")}
                    assets={logoAssets}
                    value={logoAssetId}
                    onValueChange={onLogoAssetChange}
                    error={errors.logo}
                    required
                  />
                  <div className="whitespace-pre-line rounded-lg bg-muted/40 p-4 text-sm text-muted-foreground">
                    <p className="font-medium text-foreground">
                      {t("sb.creative.logoRequirements")}
                    </p>
                    {t("sb.creative.logoSpecs")}
                  </div>
                </AccordionContent>
              </AccordionItem>
              {adFormat === "productCollection" ? (
                <>
                  <AccordionItem value="custom-image">
                    <AccordionTrigger>
                      {requiredLabel(t("sb.creative.customImage"))}
                    </AccordionTrigger>
                    <AccordionContent>
                      <AssetSelect
                        label={t("sb.creative.customImage")}
                        assets={customImageAssets}
                        value={customImageAssetId}
                        onValueChange={onCustomImageAssetChange}
                        error={errors.customImage}
                        required
                      />
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="products">
                    <AccordionTrigger>{requiredLabel(t("sb.creative.products"))}</AccordionTrigger>
                    <AccordionContent className="flex flex-col gap-3">
                      <p className="text-sm text-muted-foreground">
                        {t("sb.creative.chooseThreeProducts")}
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        {advertisedAsins.map((asin) => {
                          const checked = creativeAsins.includes(asin);
                          return (
                            <Field key={asin} orientation="horizontal">
                              <Checkbox
                                aria-label={t("sb.creative.chooseCreativeProduct", { asin })}
                                checked={checked}
                                disabled={!checked && creativeAsins.length >= 3}
                                onCheckedChange={(nextChecked) => {
                                  if (nextChecked === true) {
                                    onCreativeAsinsChange([...creativeAsins, asin].slice(0, 3));
                                    return;
                                  }
                                  onCreativeAsinsChange(
                                    creativeAsins.filter((currentAsin) => currentAsin !== asin),
                                  );
                                }}
                              />
                              <FieldLabel>{asin}</FieldLabel>
                            </Field>
                          );
                        })}
                      </div>
                      {advertisedAsins.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          {t("sb.creative.addProductsFirst")}
                        </p>
                      ) : null}
                      {errors.products ? (
                        <p className="text-xs text-destructive-foreground">{errors.products}</p>
                      ) : null}
                    </AccordionContent>
                  </AccordionItem>
                </>
              ) : null}
              {adFormat === "storeSpotlight" ? (
                <AccordionItem value="store-pages">
                  <AccordionTrigger>{requiredLabel(t("sb.creative.storePages"))}</AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-3">
                    {selectedStore?.pages.map((page) => {
                      const checked = spotlightPageIds.includes(page.id);
                      return (
                        <Field key={page.id} orientation="horizontal">
                          <Checkbox
                            checked={checked}
                            disabled={page.asins.length === 0}
                            onCheckedChange={(nextChecked) => {
                              if (nextChecked === true) {
                                onSpotlightPageIdsChange(
                                  [
                                    ...spotlightPageIds.filter((id) => id !== page.id),
                                    page.id,
                                  ].slice(-3),
                                );
                                return;
                              }
                              onSpotlightPageIdsChange(
                                spotlightPageIds.filter((id) => id !== page.id),
                              );
                              const nextAsins = { ...spotlightPageAsins };
                              delete nextAsins[page.id];
                              onSpotlightPageAsinsChange(nextAsins);
                            }}
                          />
                          <div className="grid flex-1 grid-cols-[1fr_220px] items-center gap-3">
                            <FieldLabel>{page.name}</FieldLabel>
                            {checked ? (
                              <Select
                                items={[
                                  {
                                    value: null,
                                    label: t("sb.creative.choosePageAsinPlaceholder"),
                                  },
                                  ...page.asins.map((asin) => ({ value: asin, label: asin })),
                                ]}
                                value={spotlightPageAsins[page.id] ?? null}
                                onValueChange={(value) =>
                                  onSpotlightPageAsinsChange({
                                    ...spotlightPageAsins,
                                    [page.id]: value ?? "",
                                  })
                                }
                              >
                                <SelectTrigger
                                  aria-label={t("sb.creative.choosePageAsin", {
                                    page: page.name,
                                  })}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectGroup>
                                    <SelectItem value={null}>
                                      {t("sb.creative.choosePageAsinPlaceholder")}
                                    </SelectItem>
                                    {page.asins.map((asin) => (
                                      <SelectItem
                                        key={asin}
                                        value={asin}
                                        disabled={Object.entries(spotlightPageAsins).some(
                                          ([pageId, selectedAsin]) =>
                                            pageId !== page.id && selectedAsin === asin,
                                        )}
                                      >
                                        {asin}
                                      </SelectItem>
                                    ))}
                                  </SelectGroup>
                                </SelectContent>
                              </Select>
                            ) : null}
                          </div>
                        </Field>
                      );
                    }) ?? (
                      <p className="text-sm text-muted-foreground">
                        {t("sb.blockers.storeSpotlightData")}
                      </p>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ) : null}
              <AccordionItem value="headline">
                <AccordionTrigger>{requiredLabel(t("sb.creative.headline"))}</AccordionTrigger>
                <AccordionContent>
                  <TextField
                    name="headline"
                    label={t("sb.creative.headline")}
                    value={headline}
                    onChange={onHeadlineChange}
                    error={errors.headline}
                    required
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}
        </div>
        <SponsoredBrandsAdPreview
          adFormat={adFormat}
          brandName={brandName}
          headline={headline}
          logoAsset={logoAssets.find((asset) => asset.assetId === logoAssetId) ?? null}
          featureAsset={
            (adFormat === "video"
              ? videoAssets.find((asset) => asset.assetId === videoAssetId)
              : customImageAssets.find((asset) => asset.assetId === customImageAssetId)) ?? null
          }
          creativeAsins={creativeAsins}
        />
      </CardContent>
    </Card>
  );
}
